import { GradientMesh } from '@merritrac/shared/components';
import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router';

import { usePortalAuth } from '../../hooks/usePortalAuth.js';

import type { ReactElement } from 'react';

interface ProtectedRouteProps {
  children: ReactElement;
}

export function ProtectedRoute({ children }: ProtectedRouteProps): ReactElement {
  const isAuthenticated = usePortalAuth((s) => s.isAuthenticated);
  const user = usePortalAuth((s) => s.user);
  const verify = usePortalAuth((s) => s.verify);
  const [checked, setChecked] = useState(false);
  const location = useLocation();

  useEffect(() => {
    void verify().finally(() => {
      setChecked(true);
    });
  }, []);

  if (!checked) {
    return (
      <div className="min-h-screen relative flex items-center justify-center">
        <GradientMesh />
        <div className="flex flex-col items-center gap-3 relative z-10">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Redirect unit owners away from PM-only routes to their dashboard
  if (user?.portalRole === 'unit_owner') {
    const pmOnlyPaths = ['/', '/contacts', '/work-order/new', '/documents'];
    const isPmRoute =
      pmOnlyPaths.includes(location.pathname) || location.pathname.startsWith('/reports/');
    if (isPmRoute) {
      return <Navigate to="/my-requests" replace />;
    }
  }

  // Redirect PMs away from unit-owner-only routes
  if (user?.portalRole === 'property_manager') {
    if (location.pathname.startsWith('/my-requests')) {
      return <Navigate to="/" replace />;
    }
  }

  return children;
}
